import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Book, Beaker, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';

const CreateStudyBuilder = () => {
  const navigate = useNavigate();
  const [studyName, setStudyName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!studyName.trim()) {
      toast.error('Please enter a study name');
      return;
    }

    setLoading(true);
    try {
      const response = await studyBuilderService.createStudyDraft(studyName);
      toast.success('Study draft created successfully');
      navigate(`/studybuilder/${encodeURIComponent(studyName)}`);
    } catch (error) {
      const data = error.response?.data;

      // The backend returns descriptive messages in a few shapes: a field-keyed
      // `errors` dict (serializer validation, values may be a string or array),
      // or a flat `message`/`error`/`detail`/`details` string (missing fields,
      // sponsor errors, server errors). Pull out the most specific one.
      let detail;
      if (data?.errors && typeof data.errors === 'object') {
        const firstFieldError = Object.values(data.errors)[0];
        detail = Array.isArray(firstFieldError) ? firstFieldError[0] : firstFieldError;
      }
      detail = detail || data?.message || data?.error || data?.detail || data?.details;

      // Handle specific error cases
      if (error.response?.status === 409) {
        toast.error(detail || 'A study with this name already exists');
      } else if (error.response?.status === 400) {
        toast.error(detail || 'Invalid study data provided');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to create studies');
      } else {
        toast.error(detail || error.message || 'Failed to create the study. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/studybuilder" className="back-link">
        <ChevronLeft className="h-4 w-4" />
        <span>Back to Study Builder</span>
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="card overflow-hidden"
      >
        <div className="card-band">
          <h2 className="card-band-title flex items-center gap-2">
            <Beaker className="h-5 w-5" />
            Create New Study
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <label htmlFor="studyName" className="label">
            Study Name<span className="ml-1 text-red-500">*</span>
          </label>
          <div className="relative">
            <Book className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
            <input
              type="text"
              id="studyName"
              value={studyName}
              onChange={(e) => setStudyName(e.target.value)}
              className="input pl-10"
              placeholder="Enter study name"
              required
            />
          </div>
          <p className="hint">Choose a descriptive name that clearly identifies your research study</p>

          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">What happens next</p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-300">
              <li>Add arms and schedules in the study design.</li>
              <li>Put the study's assessments on its schedules.</li>
              <li>Submit the design for approval.</li>
            </ol>
          </div>

          <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-6 dark:border-gray-700">
            <button type="button" onClick={() => navigate('/studybuilder')} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Study'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateStudyBuilder;